import {
  afterNextRender,
  afterRenderEffect,
  booleanAttribute,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  linkedSignal,
  model,
  numberAttribute,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

import { MibCheckTruncateDirective } from '../../directives';
import { prefersStill } from '../../internal/motion.utils';
import { watchSize } from '../../utils/dom.utils';
import { MibIconComponent } from '../icons';
import { MibSpinnerComponent } from '../loaders';
import { MibMenuComponent, MibMenuTriggerDirective, type MibMenuEntry } from '../menu';
import { MibTooltipDirective, type TooltipPosition } from '../tooltip';
import {
  MibTab,
  MibTabDensity,
  MibTabDividers,
  MibTabFit,
  MibTabMarker,
  MibTabsBehaviour,
  MIB_TABS_BEHAVIOUR,
  MibTabSubLabels,
  TAB_DENSITY,
  TAB_DOT_INK,
  TAB_OVERFLOW_WIDTH,
} from './tabs.models';

/**
 * A row of tabs that moves the page from one view to another.
 *
 * It is a navigation, not a tablist: the view lives elsewhere — a router outlet, the screen's own
 * content — so the row is a `<nav>` of links, the open one marked `aria-current`, one stop per tab.
 *
 * Which tab is open belongs to the caller — {@link selected} binds both ways — so a screen can open
 * one from the start and remember it. Closing is the caller's too: the row says which tab was asked
 * to close and takes nothing out of {@link tabs} itself.
 *
 * A name too long for its tab is cut, and the whole tab then opens it in full on hover — only then,
 * since a tooltip repeating a name already on screen is noise.
 *
 * More tabs than the bar can hold: the row draws only the ones that fit whole — a window of them,
 * slid to hold the tab being opened, dragged freely and settled on a whole tab, or wheeled a tab at a time —
 * and an arrow at its end opens a panel listing every
 * tab, the hidden ones included, each closable from there when it is closable at all. The panel is
 * a {@link MibMenuComponent}, so it behaves as every menu of the library does.
 *
 * Tabs arrive as **data** rather than as projected elements, the shape `<mib-button-toggles>` uses.
 */
let nextId = 0;

/* The most tabs a reveal travels over, however far the tab picked from the panel is. */
const REVEAL_SPAN = 10;

@Component({
  selector: 'mib-tabs',
  imports: [
    NgTemplateOutlet,
    MibIconComponent,
    MibSpinnerComponent,
    MibCheckTruncateDirective,
    MibTooltipDirective,
    MibMenuComponent,
    MibMenuTriggerDirective,
  ],
  templateUrl: './tabs.component.html',
  host: { class: 'flex min-w-0 flex-col' },
  styleUrl: '../../internal/spinner-leave.css',
})
export class MibTabsComponent {
  /** The tabs, in the order they are drawn. */
  readonly tabs = input.required<readonly MibTab[]>();
  /** What a screen reader calls the row, a navigation landmark. */
  readonly label = input('Tabs');
  /** The value of the tab being read. Two-way: the row never picks one on its own. */
  readonly selected = model<string | undefined>(undefined);
  /** What every cross says to a screen reader. */
  readonly closeLabel = input('Close');
  /** Turns the whole row off, on top of a tab's own `disabled`. */
  readonly disabled = input(false, { transform: booleanAttribute });
  /** Where the name opens when it is too long to be drawn in full. */
  readonly tooltipPosition = input<TooltipPosition>('below');
  /** Delay before that name opens, so sweeping across the row does not flash them all. */
  readonly tooltipShowDelay = input(500, { transform: numberAttribute });
  /** What the arrow says to a screen reader. */
  readonly overflowLabel = input('Open tabs');
  /** What the arrow adds after the open tab's name when that tab is in the panel, not on the row. */
  readonly openLabel = input('open');
  /** The title of the panel the arrow opens; empty, the panel holds the list alone. */
  readonly overflowTitle = input('');
  /** What a screen reader adds to a tab that is loading, beside its name rather than in it. */
  readonly loadingLabel = input('Loading');
  /** How the open tab is marked: a line under it, or its ink alone. */
  readonly marker = input<MibTabMarker>('line');
  /** How much room a flex tab asks for, which decides how many fit before the arrow. */
  readonly density = input<MibTabDensity>('comfortable');
  /** Where a tab's `subLabel` is drawn: under its name on the row and in the panel, or in the panel only. */
  readonly subLabels = input<MibTabSubLabels>('row');
  /** Where the vertical lines go: after every tab, or once after the last tab, for a group of icon tabs. */
  readonly dividers = input<MibTabDividers>('each');
  /** How the row behaves under the pointer; only the keys written override {@link MIB_TABS_BEHAVIOUR}. */
  readonly behaviour = input<MibTabsBehaviour>({});
  protected readonly rules = computed(() => ({ ...MIB_TABS_BEHAVIOUR, ...this.behaviour() }));

  /** Asks for a tab to be closed, by its value — from its cross or from the panel. */
  readonly closed = output<string>();
  /** Emits the tab's value on every plain activation of an enabled tab, the open one included. */
  readonly activated = output<string>();

  protected readonly loadingId = `mib-tabs-loading-${nextId++}`;
  /* The tabs that just stopped loading, so a dot taking a spinner's place arrives wide and a first draw does not. */
  protected readonly heirs = linkedSignal<readonly MibTab[], ReadonlySet<string>>({
    source: this.tabs,
    computation: (tabs, previous) => {
      const was = new Set(previous?.source.filter((tab) => tab.loading).map((tab) => tab.value));
      return new Set(tabs.filter((tab) => !tab.loading && was.has(tab.value)).map((tab) => tab.value));
    },
  });

  private readonly viewport = viewChild.required<ElementRef<HTMLElement>>('viewport');
  private readonly bar = viewChild.required<ElementRef<HTMLElement>>('bar');
  private readonly menu = viewChild(MibMenuComponent);
  private readonly arrow = viewChild<ElementRef<HTMLElement>>('arrow');
  private readonly destroyRef = inject(DestroyRef);

  /* How many flex tabs are drawn. */
  private readonly windowSize = signal(Number.POSITIVE_INFINITY);

  /* The width of the content tabs, which are always drawn. */
  protected readonly contentWidth = signal(0);
  protected readonly floorClasses = computed(() => TAB_DENSITY[this.density()].bounds);

  /* The tabs the row may draw; the others live in the panel alone. */
  private readonly rowTabs = computed(() =>
    this.rules().closableInPanel ? this.tabs().filter((tab) => !tab.closable) : this.tabs(),
  );
  /* The flex tabs the row may draw: the window, the drag and the count work on these alone. */
  protected readonly rowFlex = computed(() => this.rowTabs().filter((tab) => this.fits(tab) === 'flex'));
  /* Some tab is kept in the panel whatever the room, so the arrow is always drawn. */
  private readonly panelOnly = computed(() => this.rowTabs().length < this.tabs().length);
  /* The open tab when the row never draws it, so the arrow carries its marker and its name. */
  private readonly openInPanel = computed(() =>
    this.tabs().find((tab) => tab.value === this.selected() && !this.rowTabs().includes(tab)),
  );

  /*
   Where the window starts. Opening a tab, or the room changing, slides it just far enough to hold the
   open tab, in the same pass; a drag or the wheel moves it anywhere, the open tab included out of view.
  */
  private readonly start = linkedSignal<readonly [string | undefined, number], number>({
    source: () => [this.selected(), this.windowSize()] as const,
    computation: ([open], previous) =>
      untracked(() =>
        this.holding(
          previous?.value ?? 0,
          this.rowFlex().findIndex((tab) => tab.value === open),
        ),
      ),
  });

  /* The window start nearest to `first` that holds the flex tab at `at`, or `first` when there is none. */
  private holding(first: number, at: number): number {
    const size = this.windowSize();
    let start = this.clampStart(first);
    if (at >= 0 && at < start) start = at;
    else if (at >= start + size) start = at - size + 1;
    return start;
  }

  private readonly windowStart = computed(() => this.clampStart(this.start()));

  private clampStart(first: number): number {
    return Math.min(Math.max(0, first), Math.max(0, this.rowFlex().length - this.windowSize()));
  }

  /** The flex tabs of the window, the ones the row holds at rest. */
  private readonly windowFlex = computed(() => {
    const flex = this.rowFlex();
    const size = this.windowSize();
    if (size >= flex.length) return flex;
    const first = this.windowStart();
    return flex.slice(first, first + size);
  });

  private readonly firstFlex = computed(() => this.rowTabs().findIndex((tab) => this.fits(tab) === 'flex'));
  protected readonly leading = computed(() =>
    this.rowTabs().filter(
      (tab, index) => this.fits(tab) === 'content' && (this.firstFlex() < 0 || index < this.firstFlex()),
    ),
  );
  protected readonly trailing = computed(() =>
    this.rowTabs().filter(
      (tab, index) => this.fits(tab) === 'content' && this.firstFlex() >= 0 && index > this.firstFlex(),
    ),
  );

  /*
   While a drag runs, and while it settles, every flex tab is drawn on a strip at the width the window
   drew them, offset by the pointer; `null` is the window at rest.
  */
  protected readonly stripOffset = signal<number | null>(null);
  protected readonly stripWidth = signal<number | null>(null);
  protected readonly snapping = signal(false);

  /* The tabs on the strip: every flex tab for a drag, only the stretch travelled for a reveal. */
  private readonly stripTabs = signal<readonly MibTab[] | null>(null);
  private readonly revealing = signal(false);
  protected readonly snapMs = signal(200);
  private glideTimer?: ReturnType<typeof setTimeout>;

  protected readonly drawnFlex = computed(() => this.stripTabs() ?? this.windowFlex());

  private readonly rowOverflows = computed(() => this.windowFlex().length < this.rowFlex().length);
  protected readonly leftOff = computed(() => this.rowOverflows() || this.panelOnly());

  protected readonly dragging = signal(false);
  private readonly canGrab = computed(() => this.rules().grab && this.rowOverflows());
  private drag?: { pointer: number; x: number; from: number; step: number; moved: boolean };
  private swallowClick = false;
  private wheelTravel = 0;
  private panelWasOpen = false;
  private readonly overflowingState = signal(false);
  /**
   * Whether some tab had to be left off the row, which is when the arrow is drawn. `false` until the
   * row has drawn, so an ancestor may read it before the row's inputs are bound.
   */
  readonly overflowing = this.overflowingState.asReadonly();

  protected readonly menuEntries = computed<MibMenuEntry[]>(() =>
    this.tabs()
      .filter((tab) => this.rules().panelContentTabs || this.fits(tab) !== 'content' || !this.rowTabs().includes(tab))
      .map((tab) => ({
        value: tab.value,
        label: tab.label,
        subLabel: tab.subLabel,
        removable: !!tab.closable,
        loading: !!tab.loading,
        selected: tab.value === this.selected(),
        disabled: !!tab.disabled || this.disabled(),
      })),
  );

  constructor() {
    /* The list changing does not resize the row's box, so the observer alone would miss it. */
    afterRenderEffect(() => {
      this.tabs();
      this.measure();
    });
    afterNextRender(() => {
      const bar = this.bar().nativeElement;
      this.destroyRef.onDestroy(watchSize(bar, () => this.measure()));
      /* Capturing, so the press that ended a drag never reaches a tab, nor a link's own navigation. */
      const swallow = (event: MouseEvent): void => {
        if (!this.swallowClick) return;
        this.swallowClick = false;
        event.preventDefault();
        event.stopPropagation();
      };
      bar.addEventListener('click', swallow, { capture: true });
      this.destroyRef.onDestroy(() => bar.removeEventListener('click', swallow, { capture: true }));
      this.destroyRef.onDestroy(() => clearTimeout(this.glideTimer));
    });
    /* Read after every render: a linked signal nobody reads keeps no previous list to compare with. */
    afterRenderEffect(() => this.heirs());
    afterRenderEffect(() => this.overflowingState.set(this.leftOff()));
    /* Nothing left to close from the panel: it goes. Its own effect, so opening the panel never re-runs it. */
    afterRenderEffect(() => {
      if (!this.menuEntries().some((entry) => 'removable' in entry && entry.removable)) {
        this.menu()?.close();
      }
    });
    /* After the close above, which hands focus to an arrow that may be going too: focus back on a tab. */
    afterRenderEffect(() => {
      if (!this.leftOff() && this.panelWasOpen) {
        const tabs = this.bar().nativeElement.querySelectorAll<HTMLElement>('[data-mib-tab]');
        ([...tabs].find((tab) => tab.hasAttribute('aria-current')) ?? tabs[0])?.focus();
      }
      this.panelWasOpen = this.leftOff() && !!this.menu()?.isOpen();
    });
  }

  /*
   Counts the flex tabs that fit whole above their floor. Measured on the bar, not the row: the row
   shrinks as the arrow appears, and would count against its own consequence.
  */
  private measure(): void {
    /* The drawn arrow's own width, narrower on a phone; read first so its arrival re-runs the effect calling this. */
    const arrowWidth = this.arrow()?.nativeElement.getBoundingClientRect().width || TAB_OVERFLOW_WIDTH;
    const bar = this.bar().nativeElement;
    const content = [...bar.querySelectorAll<HTMLElement>('[data-fit="content"]')].reduce(
      (width, tab) => width + tab.getBoundingClientRect().width,
      0,
    );
    this.contentWidth.set(content);
    /* A bar that is not laid out — hidden, or outside a browser — has nothing to count against. */
    if (bar.clientWidth === 0) {
      this.windowSize.set(Number.POSITIVE_INFINITY);
      return;
    }
    const room = bar.clientWidth - content;
    const floor = TAB_DENSITY[this.density()].floor;
    const count = this.rowFlex().length;
    const arrowCell = this.panelOnly() ? arrowWidth : 0;
    const fitsAll = count * floor <= room - arrowCell;
    this.windowSize.set(fitsAll ? count : Math.max(1, Math.floor((room - arrowWidth) / floor)));
  }

  /* A drag starts anywhere on a flex tab but its cross, once the pointer has moved 4px with the button held. */
  protected onPointerDown(event: PointerEvent): void {
    this.swallowClick = false;
    if (!this.canGrab() || event.button !== 0 || this.stripTabs() !== null) return;
    if ((event.target as Element).closest('button')) return;
    const step = this.step();
    this.drag = { pointer: event.pointerId, x: event.clientX, from: this.windowStart() * step, step, moved: false };
    this.dragging.set(true);
  }

  protected onPointerMove(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag || drag.pointer !== event.pointerId) return;
    const travel = event.clientX - drag.x;
    if (!drag.moved) {
      if (Math.abs(travel) < 4) return;
      drag.moved = true;
      this.stripTabs.set(this.rowFlex());
      this.snapMs.set(200);
      this.stripWidth.set(drag.step);
      this.viewport().nativeElement.setPointerCapture(event.pointerId);
    }
    const end = (this.rowFlex().length - this.windowSize()) * drag.step;
    this.stripOffset.set(Math.min(Math.max(0, drag.from - travel), end));
  }

  /* Letting go anchors the strip on the nearest whole tab, then the window takes over again. */
  protected onPointerEnd(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag || drag.pointer !== event.pointerId) return;
    this.drag = undefined;
    this.dragging.set(false);
    if (!drag.moved) return;
    this.swallowClick = event.type === 'pointerup';
    this.viewport().nativeElement.releasePointerCapture(event.pointerId);

    const target = this.clampStart(Math.round((this.stripOffset() ?? 0) / drag.step));
    const anchored = target * drag.step;
    if (anchored === this.stripOffset() || prefersStill()) {
      this.start.set(target);
      this.leaveStrip();
      return;
    }
    this.start.set(target);
    this.glide(anchored);
  }

  /* `transitionend` is what ends a glide; the timer is for a page that is not painted and never sends it. */
  private glide(offset: number): void {
    this.snapping.set(true);
    this.stripOffset.set(offset);
    clearTimeout(this.glideTimer);
    this.glideTimer = setTimeout(() => this.onSnapped(), this.snapMs() + 100);
  }

  /* A tab's own colour fading bubbles here too: only the strip's own move ends a glide. */
  protected onGlideEnd(event: TransitionEvent): void {
    if (event.target === event.currentTarget && event.propertyName === 'transform') this.onSnapped();
  }

  private onSnapped(): void {
    if (this.snapping()) this.leaveStrip();
  }

  /*
   A tab picked from the panel is travelled to rather than jumped to, over at most ten tabs from the
   side it comes from, and is open from the first frame so the row never loses it.
  */
  protected reveal(value: string): void {
    const flex = this.rowFlex();
    const size = this.windowSize();
    const from = this.windowStart();
    const target = this.holding(
      from,
      flex.findIndex((tab) => tab.value === value),
    );
    const still = prefersStill();
    if (target === from || this.stripTabs() !== null || still) {
      this.activate(value);
      return;
    }

    const step = this.step();
    const span = Math.min(Math.abs(target - from), REVEAL_SPAN);
    const forward = target > from;
    this.stripTabs.set(forward ? flex.slice(target - span, target + size) : flex.slice(target, target + span + size));
    this.stripWidth.set(step);
    this.stripOffset.set(forward ? 0 : span * step);
    this.snapMs.set(Math.min(600, 200 + span * 40));
    this.revealing.set(true);
    this.activate(value);

    /* Two frames, so the strip is painted where it starts before it is told where to go. */
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.glide(forward ? span * step : 0);
      }),
    );
  }

  private leaveStrip(): void {
    clearTimeout(this.glideTimer);
    this.snapping.set(false);
    this.stripOffset.set(null);
    this.stripWidth.set(null);
    this.stripTabs.set(null);
    this.revealing.set(false);
  }

  /* A horizontal wheel or a trackpad moves the window a tab per notch, a notch being a tab or 100px. */
  protected onWheel(event: WheelEvent): void {
    if (!this.canGrab() || this.stripOffset() !== null) return;
    const across = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0;
    if (across === 0) return;
    event.preventDefault();
    this.wheelTravel += event.deltaMode === 1 ? across * 16 : across;
    const notch = Math.min(this.step(), 100);
    const steps = Math.trunc(this.wheelTravel / notch);
    if (steps === 0) return;
    this.wheelTravel -= steps * notch;
    this.start.set(this.clampStart(this.windowStart() + steps));
  }

  /* How far a gesture travels for the window to move by one tab: the width a flex tab is drawn at. */
  private step(): number {
    const tab = this.bar().nativeElement.querySelector<HTMLElement>('[data-fit="flex"]');
    return tab?.getBoundingClientRect().width || TAB_DENSITY[this.density()].floor;
  }

  /*
   `items-stretch` and no height: a tab is as tall as the bar that holds it, which is the header's
   decision and not the row's. Nothing scrolls — what does not fit whole is not drawn — and a tab
   never overlaps the line under the row: its own 2px mark sits just above it, inside its box.
  */
  protected readonly barClasses = 'flex min-h-full min-w-0 items-stretch';

  protected readonly rowClasses = 'flex min-w-0 flex-1 items-stretch overflow-hidden border-b border-mib-divider';

  /* Grabbable only when some row tab is left off, since only then is there somewhere to move to. */
  protected readonly viewportClasses = computed(
    () =>
      'relative flex min-w-0 flex-1 items-stretch overflow-hidden' +
      (this.canGrab() ? ' touch-pan-y select-none' : '') +
      (this.dragging() ? ' cursor-grabbing' : this.canGrab() ? ' cursor-grab' : ''),
  );

  /* Out of the flow while it is a strip, so the row's own size never grows with the tabs it hides. */
  protected readonly stripClasses = computed(() => {
    if (this.stripOffset() === null) return 'flex min-w-0 flex-1 items-stretch';
    return (
      'absolute inset-y-0 left-0 flex items-stretch' +
      (this.snapping() ? ' transition-transform' + (this.revealing() ? ' ease-in-out' : ' ease-out') : '')
    );
  });

  /* A cell of the bar, as tall as the tabs, on the same line; not a button of a theme. Holding the
     open tab, it wears that tab's marker, its line drawn above the border as a tab's is. */
  protected readonly moreClasses = computed(
    () =>
      'relative flex shrink-0 cursor-pointer items-center justify-center border-x border-b border-mib-divider px-3.5 not-expanded:px-2 outline-mib-primary hover:bg-gray-100 focus-visible:outline-2 focus-visible:-outline-offset-2' +
      (!this.openInPanel()
        ? ' text-gray-500 hover:text-gray-900'
        : ' text-mib-primary' +
          (this.marker() === 'line'
            ? ' after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-mib-primary'
            : '')),
  );

  protected readonly moreLabel = computed(() => {
    const open = this.openInPanel();
    return open ? `${this.overflowLabel()}, ${open.label} ${this.openLabel()}` : this.overflowLabel();
  });

  protected readonly menuTitleClasses = 'text-xs/4.5 font-semibold tracking-widest text-gray-500 uppercase';

  /*
   A content tab is exactly as wide as what it carries. A flex tab shares the bar with the other flex
   tabs, between the two bounds its density sets, past which the row overflows and the arrow takes
   over. The 2px under the row is always reserved, so turning the marker off moves nothing.
  */
  protected tabClasses(tab: MibTab): string {
    return (
      'relative flex items-stretch border-b-2 transition-colors motion-reduce:transition-none' +
      (this.dividers() === 'each' || tab === this.rowTabs().at(-1) ? ' border-r border-r-mib-divider' : '') +
      (this.fits(tab) === 'content'
        ? ' flex-none'
        : this.stripWidth() === null
          ? ' ' + TAB_DENSITY[this.density()].bounds + ' flex-1'
          : ' flex-none') +
      (this.isOpen(tab) && this.marker() === 'line' ? ' border-b-mib-primary' : ' border-b-transparent') +
      (this.isOff(tab) ? ' opacity-60' : ' hover:bg-gray-100' + this.tabCursor(tab))
    );
  }

  /* With a cross, the name ends 8px from it — the same 8px the cross keeps from the tab's edge. */
  protected labelClasses(tab: MibTab): string {
    return (
      'flex min-w-0 flex-1 items-center gap-2 pl-4 no-underline outline-mib-primary focus-visible:outline-2 focus-visible:-outline-offset-2 ' +
      (tab.closable ? 'pr-2 ' : 'pr-4 ') +
      (tab.iconOnly ? 'not-expanded:px-3 ' : '') +
      (this.isOpen(tab) ? 'text-mib-primary' : 'text-gray-500')
    );
  }

  protected dotClasses(tab: MibTab): string {
    return 'size-2 shrink-0 rounded-full ' + TAB_DOT_INK[tab.dot as keyof typeof TAB_DOT_INK];
  }

  protected readonly crossClasses =
    'mr-2 flex size-6 shrink-0 cursor-pointer items-center justify-center self-center rounded-mib text-gray-500 outline-mib-primary hover:bg-gray-200 hover:text-gray-900 focus-visible:outline-2';

  /*
   A tab that leads somewhere is a real link, so the browser's own gestures keep working — but a
   plain click opens the tab in place and never follows the address, since `selected` is what the
   screen reads. A modified click is the browser's alone, and the tab opened beside is not the one read.
  */
  protected onTabClick(event: MouseEvent, tab: MibTab): void {
    if (this.isOff(tab)) {
      event.preventDefault();
      return;
    }
    if (this.leads(tab)) {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
    }
    this.activate(tab.value);
  }

  private activate(value: string): void {
    this.selected.set(value);
    this.activated.emit(value);
  }

  /* A link answers Enter with a click of its own; a tab with nowhere to lead is a button and answers both keys. */
  protected onTabKey(event: Event, tab: MibTab): void {
    if (this.leads(tab)) return;
    event.preventDefault();
    if (this.isOff(tab)) return;
    this.activate(tab.value);
  }

  protected leads(tab: MibTab): boolean {
    return !!tab.href && !this.isOff(tab);
  }

  protected isOff(tab: MibTab): boolean {
    return !!tab.disabled || this.disabled();
  }

  /* `active:` because the browser draws the cursor on a pointer event, before any render could change it. */
  private tabCursor(tab: MibTab): string {
    if (this.fits(tab) === 'content') return ' cursor-pointer';
    if (this.dragging()) return ' cursor-grabbing';
    return this.canGrab() ? ' cursor-grab active:cursor-grabbing' : ' cursor-pointer';
  }

  /* The middle button closes a closable tab, as in a browser, instead of scrolling or opening a window. */
  protected onTabMouseDown(event: MouseEvent, tab: MibTab): void {
    if (event.button === 1 && tab.closable && this.rules().closeOnMiddleClick) event.preventDefault();
  }

  protected onTabAuxClick(event: MouseEvent, tab: MibTab): void {
    if (event.button !== 1 || !tab.closable || !this.rules().closeOnMiddleClick) return;
    event.preventDefault();
    this.closed.emit(tab.value);
  }

  protected isOpen(tab: MibTab): boolean {
    return this.selected() === tab.value;
  }

  /* An icon alone has nothing to share the room with. */
  protected fits(tab: MibTab): MibTabFit {
    return tab.iconOnly ? 'content' : (tab.fit ?? 'flex');
  }
}
